import { useState, useEffect } from 'react';
import { GetConsolidatedLog, constructData } from '../ConsolidatedController';
import { getComplianceConfig } from '../../common/service/dataOperations';
import { KpiDashboardConfig } from './kpiConfig';
import { useKpiConfig } from './useKpiConfig';

// Keyed by short name — maps to the exact logTitle strings the API expects.
export const LOG_TITLES: Record<string, string> = {
  observations: 'Environmental Issues Observations',
  energy:        'Energy Log',
  water:         'Water Log',
  waste:         'Waste Tracking Log',
  wastewater:    'Wastewater Tracking Log',
  spoil:         'Spoil Tracking Log',
  hazardous:     'Hazardous Material Storage Log',
  importedSoil:  'Imported Soil Log',
  recycled:      'Recycled Materials Log',
  regional:      'Regional Materials Log',
  training:      'Training Log',
  noise:         'Noise Monitoring Log',
  vibration:     'Vibration Monitoring Log',
  pm10:          'PM10 Monitoring Log',
  pm25:          'PM2.5 Monitoring Log',
};

export type RawDashboardData = { [K in keyof typeof LOG_TITLES]: any[] };

export interface UseDashboardKPIDataResult {
  rawData: RawDashboardData | null;
  complianceConfig: any | null;
  kpiConfig: KpiDashboardConfig;
  isLoading: boolean;
  error: string | null;
}

export function useDashboardKPIData(
  accessToken: string | null,
  clientAPIURL: string,
  projectId: string,
): UseDashboardKPIDataResult {
  const [rawData, setRawData] = useState<RawDashboardData | null>(null);
  const [complianceConfig, setComplianceConfig] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const kpiConfig = useKpiConfig(clientAPIURL, accessToken, projectId);

  useEffect(() => {
    if (!accessToken || !clientAPIURL || !projectId) return;
    setIsLoading(true);
    setError(null);

    const keys = Object.keys(LOG_TITLES);

    Promise.all([
      Promise.all(
        keys.map(key =>
          GetConsolidatedLog(LOG_TITLES[key], clientAPIURL, accessToken, projectId)
            .then(resp => ({ key, items: constructData(resp).items ?? [] }))
            .catch(() => ({ key, items: [] as any[] })),
        ),
      ),
      getComplianceConfig(clientAPIURL, accessToken, projectId).catch(() => null),
    ])
      .then(([results, config]) => {
        const data = {} as RawDashboardData;
        results.forEach(({ key, items }) => { (data as any)[key] = items; });
        setRawData(data);
        setComplianceConfig(config);
      })
      .catch(() => setError('Failed to load KPI data'))
      .finally(() => setIsLoading(false));
  }, [accessToken, clientAPIURL, projectId]);

  return { rawData, complianceConfig, kpiConfig, isLoading, error };
}
